// frontend/src/index.js
// Acts as the entry point for your React app. It renders the root component and wraps your app in Redux’s <Provider> so that every component can access the Redux store.
// Entry point for the React app—loads Tailwind and Socket.IO, configures Axios (base URL, auth/refresh interceptors), restores session, and renders <App /> inside the Redux <Provider>.
import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { Provider } from 'react-redux';
import { fetchCurrentUser } from './features/auth/authSlice';

import App from './App';
import store from './store';

// 1️⃣ Pull in Tailwind’s generated CSS
import './index.css'; 

// 2️⃣ Initialize Socket.IO client (connects automatically)
import './services/socket';

// — Axios global defaults —
// 1️⃣ Base URL for all API calls
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// 2️⃣ Send cookies (for refresh token) on every request
axios.defaults.withCredentials = true;

// 3️⃣ Attach Authorization header using stored access token
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  err => Promise.reject(err)
);

// — Prime session on app startup —
const existingToken = localStorage.getItem('accessToken');
if (existingToken) {
  axios.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
  // Dispatch to load user into Redux
  store.dispatch(fetchCurrentUser());
}

// 4️⃣ Response interceptor: handle 401 by attempting a refresh for non-auth endpoints
axios.interceptors.response.use(
  response => response,
  async error => {
      // ←—— LOG STATUS + URL of every failed request
      console.error(
        'Got HTTP',
        error.response?.status,
        'from',
        error.config.method?.toUpperCase(),
        error.config.url
      );
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest.url || '';

    // Avoid infinite loop: don't refresh on login, refresh, or me endpoints
    const isLogin   = url.endsWith('/auth/login');
    const isRefresh = url.endsWith('/auth/refresh');
    const isWhoami  = url.endsWith('/auth/me');

    if (
      status === 401 &&
      !originalRequest._retry &&
      !isLogin &&
      !isRefresh &&
      !isWhoami
    ) {
      originalRequest._retry = true;
      try {
        // Attempt token refresh (cookie auto-sent)
        const { data } = await axios.post('/auth/refresh');
        const { accessToken } = data;

        // Persist and prime for future requests
        localStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Retry the original request
        return axios(originalRequest);
      } catch (refreshError) {
        // Refresh failed — forward the original error
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <App />
  </Provider>
);
